import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../api';

interface AuthUser {
  id: number;
  email: string;
  role: string;
  staffName?: string;
  staffId?: number;
  isPartner?: boolean;
  /**
   * Field staff — farm and site labour who only ever punch attendance. The app
   * shows them one screen and nothing else, so this has to survive a reload:
   * it comes from the token, not from a route guard.
   */
  isFieldStaff?: boolean;
  // Which firm this user belongs to. Drives the firm name shown in the app
  // shell and every fallback that used to be hardcoded to MGSG.
  tenantId?: number;
  tenantName?: string;
  tenantSlug?: string;
}

/**
 * Which of their two hats a user who is both Admin and a partner is currently
 * wearing. Only ever 'admin' by a deliberate flip of the sidebar switch.
 */
export type ViewMode = 'partner' | 'admin';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  /** Finish a sign-in with a one-time code emailed to the user. */
  loginWithOtp: (email: string, code: string) => Promise<void>;
  /** Adopt a session obtained some other way (currently: a passkey). */
  applySession: (data: any) => void;
  logout: () => void;
  /**
   * Follows the hat currently on, not the role on the token: for a user who is
   * both Admin and a partner this is false in partner view. Every existing
   * `isAdmin` check therefore falls back to the partner path automatically,
   * which is the whole mechanism — there is no second set of view flags.
   *
   * Partner view is a faithful partner experience precisely because partners
   * genuinely hold the billing permissions now (server-side too); what drops
   * away is the administrative surface, deletions, and opening-balance
   * corrections, which are what Admin actually adds.
   */
  isAdmin: boolean;
  /** The signed-in user's firm, for display. Empty before login. */
  firmName: string;
  isHR: boolean;
  isPartner: boolean;
  /** True for staff who may only reach the attendance screen. */
  isFieldStaff: boolean;
  /** True only for the Admin-and-partner users who actually have both views. */
  canSwitchView: boolean;
  viewMode: ViewMode;
  /** Persists the choice and reloads, so every page refetches under the new hat. */
  setViewMode: (mode: ViewMode) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Partner view is the default: an Admin who is also a partner spends most of
  // their day being a partner, and the admin surface is noise until they want it.
  const [viewMode, setViewModeState] = useState<ViewMode>(
    () => (localStorage.getItem('viewMode') === 'admin' ? 'admin' : 'partner'),
  );

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api
        .get('/auth/profile')
        .then((res) => {
          // Uploaded files are fetched by the browser via <img>/<a>, which
          // can't send the Authorization header, so they use this separate
          // short-scoped token in the query string instead (see uploadsUrl).
          if (res.data.fileToken) localStorage.setItem('fileToken', res.data.fileToken);
          setUser({
            id: res.data.id,
            email: res.data.email,
            role: res.data.role,
            staffName: res.data.staff?.staffName,
            staffId: res.data.staffId,
            isPartner: res.data.isPartner ?? res.data.staff?.isPartner ?? false,
            isFieldStaff: res.data.isFieldStaff ?? res.data.staff?.isFieldStaff ?? false,
            tenantId: res.data.tenantId,
            tenantName: res.data.tenant?.name,
            tenantSlug: res.data.tenant?.slug,
          });
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('fileToken');
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];
          delete api.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  // Both sign-in paths return the same payload, so they share one handler.
  const applySession = (data: any) => {
    const { token: newToken, fileToken, user: newUser } = data;
    localStorage.setItem('token', newToken);
    if (fileToken) localStorage.setItem('fileToken', fileToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
  };

  const login = async (email: string, password: string) => {
    const res = await api.post('/auth/login', { email, password });
    applySession(res.data);
  };

  const loginWithOtp = async (email: string, code: string) => {
    const res = await api.post('/auth/otp/verify', { email, code });
    applySession(res.data);
  };

  // A flip changes what half the app fetches, so reload rather than trying to
  // re-run every page's effects: switching hats is rare and deliberate.
  const setViewMode = (mode: ViewMode) => {
    localStorage.setItem('viewMode', mode);
    setViewModeState(mode);
    window.location.reload();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('fileToken');
    // The next person to sign in on this browser gets their own default.
    localStorage.removeItem('viewMode');
    delete axios.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasAdminRole = user?.role === 'ADMIN';
  const isPartner = user?.isPartner ?? false;
  // Only someone holding both hats has anything to switch between. A plain
  // Admin, or a plain partner, is always already in their one and only view.
  const canSwitchView = hasAdminRole && isPartner;
  // In partner view the admin role is simply set aside, so the entire app —
  // pages, route guards, dashboards, every action — renders as it would for a
  // partner, with no per-screen special cases to keep in sync.
  const isAdmin = canSwitchView ? viewMode === 'admin' : hasAdminRole;

  return (
    <AuthContext.Provider value={{
      user, token, login, loginWithOtp, applySession, logout, loading,
      firmName: user?.tenantName ?? '',
      isAdmin,
      isHR: user?.role === 'HR' || isAdmin,
      isPartner,
      isFieldStaff: user?.isFieldStaff ?? false,
      canSwitchView,
      viewMode,
      setViewMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
